import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { useConfiguration } from "@/api/configuration";
import { useStockValueHistory } from "@/api/dashboard";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Empty, EmptyDescription, EmptyHeader } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency } from "@/utils/formatters";

export function StockValueCard() {
  const { t } = useTranslation();
  const { data: config } = useConfiguration();
  const currency = config?.currencyCode ?? "EUR";
  const { data } = useStockValueHistory();
  const points = data?.points;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("dashboard.stockValue.title")}</CardTitle>
        {!!data?.uncostedItems && (
          <CardDescription>
            {t("dashboard.stockValue.uncosted", { count: data.uncostedItems })}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {!points ? (
          <Skeleton className="h-64 w-full" />
        ) : points.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyDescription>
                {t("dashboard.stockValue.empty")}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="h-64">
            <Line
              options={{
                animation: false,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  // Points sit on month ends; label each tick with its own point's month.
                  x: {
                    type: "time",
                    time: { unit: "month" },
                    ticks: { source: "data" },
                  },
                  y: {
                    min: 0,
                    ticks: {
                      callback: (v) =>
                        formatCurrency(Number(v), currency, {
                          maximumFractionDigits: 0,
                        }),
                    },
                  },
                },
              }}
              data={{
                datasets: [
                  {
                    data: points.map((p) => ({ x: p.date, y: p.value })),
                    pointRadius: 0,
                    tension: 0.4,
                    fill: true,
                    // Brand coral, as in the item stock chart; canvas can't read CSS tokens.
                    borderColor: "#f27a61",
                    backgroundColor: "rgba(242, 122, 97, 0.12)",
                  },
                ],
              }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
